import {useMemo, useState} from 'react';
import {useQuery, type UseQueryOptions} from '@tanstack/react-query';
import {type QueryError} from '../../lib/queryClient';
import {isAutoListQueryValid} from '../../utils/autoList';
import {useDebouncedQueryString} from './useDebouncedQueryString';
import {useReactQueryAutoListUtils} from './useReactQueryAutoListUtils';

type PromiseType<T extends Promise<any>> =
	T extends Promise<infer U> ? U : never;

type QueryFnFirstParam<T = {}> = {query: string} & T;

type QueryFn<T extends QueryFnFirstParam<any>> = (
	params: T,
	signal?: AbortSignal,
) => Promise<any>;

type HasAdditionalProps<T> = keyof Omit<T, 'query'> extends never
	? false
	: true;

export type UseAutoListParams<
	F extends QueryFn<any>,
	P extends Omit<Parameters<F>[0], 'query'> = any,
	Required extends boolean = HasAdditionalProps<Parameters<F>[0]>,
> = {
	queryFn: F;
	queryOptions?: Partial<
		UseQueryOptions<
			PromiseType<ReturnType<F>>,
			QueryError,
			PromiseType<ReturnType<F>>,
			[string, QueryFnFirstParam<any>]
		>
	>;
	filterOptions?: (
		options: PromiseType<ReturnType<F>>,
	) => PromiseType<ReturnType<F>>;
	isQueryValid?: (query?: string) => boolean;
} & (Required extends true ? {params: P} : {params?: P});

export const useAutoList = <
	F extends QueryFn<any>,
	P extends Omit<Parameters<F>[0], 'query'>,
>({
	queryOptions,
	params,
	filterOptions,
	isQueryValid = isAutoListQueryValid,
	queryFn,
}: UseAutoListParams<F, P>) => {
	const [isOpen, setIsOpen] = useState(false);

	const {debouncedQueryStr, queryStr, queryIsValid, setQueryStr} =
		useDebouncedQueryString({
			isQueryValid,
		});

	const _params = useMemo(
		() => ({...params, query: debouncedQueryStr ?? '""'}),
		[params, debouncedQueryStr],
	);

	const query = useQuery<
		PromiseType<ReturnType<F>>,
		QueryError,
		PromiseType<ReturnType<F>>,
		[string, QueryFnFirstParam<any>]
	>({
		queryKey: ['auto-list', _params],
		queryFn: ({queryKey, signal}) => queryFn({...queryKey[1]}, signal),
		select: (data) => (filterOptions ? filterOptions(data) : data),
		enabled: isOpen && queryIsValid,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		keepPreviousData: isOpen,
		...queryOptions,
	});

	const {loadingOptions} = useReactQueryAutoListUtils({
		clearOptions: !isOpen,
		isOpen,
		query,
		queryIsValid,
	});

	return {
		query,
		loadingOptions,
		queryIsValid,
		queryStr,
		setQueryStr,
		setIsOpen,
	};
};
